import * as React from 'react';
import { Box, FormControlLabel } from '@mui/material';
import {
  GRID_TREE_DATA_GROUPING_FIELD,
  GridColDef,
  GridPanelContent,
  GridPanelFooter,
  GridPanelHeader,
  GridPanelWrapper,
  GridPanelWrapperProps,
  GridStateColDef,
  gridColumnDefinitionsSelector,
  gridColumnVisibilityModelSelector,
  useGridApiContext,
  useGridRootProps,
  useGridSelector
} from '@mui/x-data-grid-pro';

const collator = new Intl.Collator();

const defaultSearchPredicate = (column: GridStateColDef, searchValue: string): boolean =>
  (column.headerName || column.field).toLowerCase().indexOf(searchValue) > -1;

interface Props extends GridPanelWrapperProps {
  sort?: 'asc' | 'desc';
  searchPredicate?: (column: GridStateColDef, searchValue: string) => boolean;
  autoFocusSearchField?: boolean;
}

/*
 * OSI-808: собственная копия штатной GridColumnsPanel (@mui/x-data-grid-pro 5.17.26,
 * исходник — .yarn/cache/@mui-x-data-grid-npm-5.17.26-*.zip → components/panel/GridColumnsPanel.js),
 * из списка которой исключена служебная колонка группировки дерева __tree_data_group__.
 *
 * Почему нельзя было обойтись hideable: false на groupingColDef (OsvGrid.tsx):
 * штатная GridColumnsPanel строит список колонок из gridColumnDefinitionsSelector БЕЗ какой-либо
 * фильтрации по hideable — этот флаг только блокирует переключатель у уже отрисованной строки,
 * саму строку он не убирает. Строка с полем __tree_data_group__ (и подписью "__tree_data_group__",
 * т.к. у groupingColDef headerName: '') всё равно осталась бы видна в списке.
 *
 * Почему саму колонку нельзя скрыть через columnVisibilityModel/setColumnVisibility:
 * через неё рендерятся стрелки разворачивания веток дерева (GridTreeDataGroupingCell) и чекбокс
 * "развернуть всё" в шапке (renderHeader у groupingColDef в OsvGrid.tsx) — при скрытии колонки
 * дерево перестанет разворачиваться.
 *
 * Решение: колонку не трогаем и не прячем, а фильтруем её из списка панели ДО сортировки и поиска.
 * "Скрыть все"/"Показать все" (toggleAllColumns) тоже собраны на этом же отфильтрованном массиве,
 * поэтому они физически не могут задеть служебное поле — в исходной реализации именно "Скрыть все"
 * прятало и колонку дерева, ломая раскрытие/сворачивание.
 */
export const OsvColumnsPanel: React.FC<Props> = (props) => {
  const { sort, searchPredicate = defaultSearchPredicate, autoFocusSearchField = true, ...other } = props;

  const apiRef = useGridApiContext();
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const firstSwitchRef = React.useRef<HTMLInputElement>(null);
  const allColumns = useGridSelector(apiRef, gridColumnDefinitionsSelector);
  const columnVisibilityModel = useGridSelector(apiRef, gridColumnVisibilityModelSelector);
  const rootProps = useGridRootProps();
  const [searchValue, setSearchValue] = React.useState('');

  // OSI-808: единственное отличие от штатной панели — служебное поле группировки дерева
  // убирается из списка здесь, до сортировки/поиска, чтобы не попасть в панель ни при каких sort/searchPredicate.
  const columns = React.useMemo(
    () => allColumns.filter((column) => column.field !== GRID_TREE_DATA_GROUPING_FIELD),
    [allColumns]
  );

  const sortedColumns = React.useMemo(() => {
    switch (sort) {
      case 'asc':
        return [...columns].sort((a, b) => collator.compare(a.headerName || a.field, b.headerName || b.field));
      case 'desc':
        return [...columns].sort((a, b) => -collator.compare(a.headerName || a.field, b.headerName || b.field));
      default:
        return columns;
    }
  }, [columns, sort]);

  const toggleColumn = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name: field } = event.target;
    apiRef.current.setColumnVisibility(field, columnVisibilityModel[field] === false);
  };

  const toggleAllColumns = React.useCallback(
    (isVisible: boolean) => {
      if (apiRef.current.unstable_caches.columns.isUsingColumnVisibilityModel) {
        const currentModel = gridColumnVisibilityModelSelector(apiRef);
        const newModel = { ...currentModel };

        // OSI-808: перебор идёт по columns (уже без __tree_data_group__), поэтому служебная
        // колонка дерева никогда не попадёт в newModel и не будет скрыта кнопкой "Скрыть все".
        columns.forEach((col) => {
          if (col.hideable) {
            if (isVisible) {
              delete newModel[col.field];
            } else {
              newModel[col.field] = false;
            }
          }
        });
        apiRef.current.setColumnVisibilityModel(newModel);
        return;
      }

      const nextColumns: GridColDef[] = columns.map((col) =>
        col.hideable !== false ? { field: col.field, hide: !isVisible } : col
      );
      apiRef.current.updateColumns(nextColumns);
    },
    [apiRef, columns]
  );

  const handleSearchValueChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  }, []);

  const currentColumns = React.useMemo(() => {
    if (!searchValue) {
      return sortedColumns;
    }
    const searchValueToCheck = searchValue.toLowerCase();
    return sortedColumns.filter((column) => searchPredicate(column, searchValueToCheck));
  }, [sortedColumns, searchValue, searchPredicate]);

  React.useEffect(() => {
    if (autoFocusSearchField) {
      searchInputRef.current?.focus();
    } else if (firstSwitchRef.current && typeof firstSwitchRef.current.focus === 'function') {
      firstSwitchRef.current.focus();
    }
  }, [autoFocusSearchField]);

  let firstHideableColumnFound = false;
  const isFirstHideableColumn = (column: GridStateColDef): boolean => {
    if (!firstHideableColumnFound && column.hideable !== false) {
      firstHideableColumnFound = true;
      return true;
    }
    return false;
  };

  return (
    <GridPanelWrapper {...other}>
      <GridPanelHeader>
        <rootProps.components.BaseTextField
          label={apiRef.current.getLocaleText('columnsPanelTextFieldLabel')}
          placeholder={apiRef.current.getLocaleText('columnsPanelTextFieldPlaceholder')}
          inputRef={searchInputRef}
          value={searchValue}
          onChange={handleSearchValueChange}
          variant="standard"
          fullWidth
          {...rootProps.componentsProps?.baseTextField}
        />
      </GridPanelHeader>
      <GridPanelContent>
        <Box sx={{ padding: '8px 0px 8px 8px' }}>
          {currentColumns.map((column) => (
            <Box
              key={column.field}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '1px 8px 1px 7px'
              }}
            >
              <FormControlLabel
                control={
                  <rootProps.components.BaseSwitch
                    disabled={column.hideable === false}
                    checked={columnVisibilityModel[column.field] !== false}
                    onClick={toggleColumn}
                    name={column.field}
                    size="small"
                    inputRef={isFirstHideableColumn(column) ? firstSwitchRef : undefined}
                    {...rootProps.componentsProps?.baseSwitch}
                  />
                }
                label={column.headerName || column.field}
              />
            </Box>
          ))}
        </Box>
      </GridPanelContent>
      <GridPanelFooter>
        <rootProps.components.BaseButton onClick={() => toggleAllColumns(false)} {...rootProps.componentsProps?.baseButton}>
          {apiRef.current.getLocaleText('columnsPanelHideAllButton')}
        </rootProps.components.BaseButton>
        <rootProps.components.BaseButton onClick={() => toggleAllColumns(true)} {...rootProps.componentsProps?.baseButton}>
          {apiRef.current.getLocaleText('columnsPanelShowAllButton')}
        </rootProps.components.BaseButton>
      </GridPanelFooter>
    </GridPanelWrapper>
  );
};
